import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Store,
  CreditCard,
  Truck,
  Bell,
  Globe,
  Save,
  Loader2,
  Plus,
  Code,
  Copy,
  Check,
  ChevronDown,
  AlertTriangle,
  FlaskConical,
  X,
  Search,
} from 'lucide-react';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { isVersionConflict, conflictCurrentVersion, apiErrorMessage } from '@/lib/conflict';
import { RefreshingHint } from '@/components/common/DataState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import type { ShippingZone } from '@/types';
import { ShippingZoneEditor } from '@/components/ecommerce/ShippingZoneEditor';
import { ZONE_TEMPLATES } from '@/lib/shipping/templates';
import { getAllCountries, getCountryMeta } from '@/lib/shipping/region-catalog';
import { WebhookHealthBanner } from './WebhookHealthBanner';

// Global (project-independent) storefront integration spec. No projectId, so
// this isn't in the per-project qk factory yet.
const STOREFRONT_SPEC_KEY = qk.storefrontSpec(); // promoted to central factory

// ── Shipping Countries Picker ──

const ALL_COUNTRIES = getAllCountries();

function ShippingCountriesCard({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (codes: string[]) => void;
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = ALL_COUNTRIES.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.country.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (code: string) => {
    if (selected.includes(code)) {
      onChange(selected.filter((c) => c !== code));
    } else {
      onChange([...selected, code]);
    }
  };

  const remove = (code: string) => {
    onChange(selected.filter((c) => c !== code));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Shipping Countries</CardTitle>
        <CardDescription>
          Select which countries your store ships to. Customers will see these as options in the
          checkout country dropdown. Their country will be auto-detected and pre-selected when
          possible.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Selected countries as pills */}
        {selected.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {selected.map((code) => {
              const meta = getCountryMeta(code);
              return (
                <Badge
                  key={code}
                  variant="secondary"
                  className="hover:bg-destructive/10 cursor-pointer py-1 pl-2 pr-1 text-sm"
                  onClick={() => remove(code)}
                >
                  {meta ? `${meta.flag} ${meta.name}` : code}
                  <X className="ml-1 h-3 w-3" />
                </Badge>
              );
            })}
          </div>
        )}

        {/* Search + dropdown */}
        <div className="relative" ref={dropdownRef}>
          <div className="relative">
            <Search className="text-muted-foreground absolute left-2.5 top-2.5 h-4 w-4" />
            <Input
              placeholder="Search countries..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              className="pl-8"
            />
          </div>

          {open && (
            <div className="bg-popover absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-md border shadow-lg">
              {filtered.length === 0 ? (
                <div className="text-muted-foreground px-3 py-2 text-sm">No countries found</div>
              ) : (
                filtered.map((c) => {
                  const isSelected = selected.includes(c.country);
                  return (
                    <button
                      key={c.country}
                      type="button"
                      className={`hover:bg-accent flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
                        isSelected ? 'bg-accent/50' : ''
                      }`}
                      onClick={() => {
                        toggle(c.country);
                        setSearch('');
                      }}
                    >
                      <span className="w-5 text-center">
                        {isSelected ? (
                          <Check className="text-primary h-4 w-4" />
                        ) : (
                          <span>{c.flag}</span>
                        )}
                      </span>
                      <span>{c.name}</span>
                      <span className="text-muted-foreground ml-auto text-xs">{c.country}</span>
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>

        {selected.length === 0 && (
          <p className="text-muted-foreground text-sm">
            No countries selected. Customers will see all countries at checkout.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ── Constants ──

const COMMON_TAX_NAMES = [
  { value: 'GST', label: 'Goods and Services Tax' },
  { value: 'VAT', label: 'Value Added Tax' },
  { value: 'Sales Tax', label: 'State Sales Tax' },
  { value: 'HST', label: 'Harmonized Sales Tax' },
  { value: 'CT', label: 'Consumption Tax' },
  { value: 'MwSt', label: 'Mehrwertsteuer' },
  { value: 'TVA', label: 'Taxe sur la valeur ajoutée' },
  { value: 'IVA', label: 'Impuesto sobre el Valor Añadido' },
  { value: 'BTW', label: 'Belasting over de Toegevoegde Waarde' },
  { value: 'Moms', label: 'Mervärdesskatt' },
];

// ── Types ──

interface SEO {
  metaTitle: string;
  metaDescription: string;
}

interface StoreSettings {
  /** Optimistic-concurrency counter from the server; absent (fresh store) = 0. */
  version?: number;
  storeName: string;
  storeEmail: string;
  storePhone: string;
  currency: string;
  taxName: string;
  taxRate: number;
  taxInclusive: boolean;
  shippingZones: ShippingZone[];
  shippingCountries: string[];
  requireShippingAddress: boolean;
  requirePhone: boolean;
  enableGuestCheckout: boolean;
  paymentGateway: 'razorpay' | 'stripe' | 'none';
  /**
   * Toggles which set of gateway credentials (live vs. test/sandbox) the
   * storefront uses for the next checkout. Both Stripe and Razorpay issue
   * separate sandbox keys (pk_test_* / sk_test_* / rzp_test_*) that transact
   * on a simulated network — zero real money moves, and you can use test
   * card numbers from each gateway's docs.
   */
  paymentMode: 'live' | 'test';
  enableCod: boolean;
  enableWhatsapp: boolean;
  whatsappNumber: string;
  // Live credentials
  razorpayKeyId: string;
  razorpayKeySecret: string;
  razorpayWebhookSecret: string;
  stripePublishableKey: string;
  stripeSecretKey: string;
  stripeWebhookSecret: string;
  // Test / sandbox credentials (independent — admins can fill both at once)
  razorpayTestKeyId: string;
  razorpayTestKeySecret: string;
  razorpayTestWebhookSecret: string;
  stripeTestPublishableKey: string;
  stripeTestSecretKey: string;
  stripeTestWebhookSecret: string;
  orderConfirmationEmail: boolean;
  orderShippedEmail: boolean;
  orderDeliveredEmail: boolean;
  orderCancelledEmail: boolean;
  lowStockAlertEmail: boolean;
  newOrderAlertEmail: boolean;
  cartRecoveryEmail: boolean;
  reviewAskEmail: boolean;
  reviewAskDelayDays: number;
  reviewAskTrigger: 'paid' | 'shipped' | 'delivered';
  reviewRewardCouponCode: string;
  /** Optional: absent = no structured policy yet (key omitted from the PUT). */
  returnPolicy?: {
    category: 'not-permitted' | 'finite';
    merchantReturnDays?: number;
    returnFees?: 'free' | 'customer-paid';
    defectiveItemWindowDays?: number;
    applicableCountry?: string;
  };
  showReviewsOnProductPage: boolean;
  autoPublishReviews: boolean;
  seo: SEO;
}

const defaultSettings: StoreSettings = {
  storeName: '',
  storeEmail: '',
  storePhone: '',
  currency: 'INR',
  taxName: 'Tax',
  taxRate: 0,
  taxInclusive: false,
  shippingZones: [],
  shippingCountries: [],
  requireShippingAddress: true,
  requirePhone: false,
  enableGuestCheckout: true,
  paymentGateway: 'none',
  paymentMode: 'live',
  enableCod: false,
  enableWhatsapp: false,
  whatsappNumber: '',
  razorpayKeyId: '',
  razorpayKeySecret: '',
  razorpayWebhookSecret: '',
  stripePublishableKey: '',
  stripeSecretKey: '',
  stripeWebhookSecret: '',
  razorpayTestKeyId: '',
  razorpayTestKeySecret: '',
  razorpayTestWebhookSecret: '',
  stripeTestPublishableKey: '',
  stripeTestSecretKey: '',
  stripeTestWebhookSecret: '',
  orderConfirmationEmail: true,
  orderShippedEmail: true,
  orderDeliveredEmail: true,
  orderCancelledEmail: true,
  lowStockAlertEmail: true,
  newOrderAlertEmail: true,
  // Customer-visible marketing emails: opt-in, default OFF.
  cartRecoveryEmail: false,
  reviewAskEmail: false,
  reviewAskDelayDays: 5,
  reviewAskTrigger: 'delivered',
  reviewRewardCouponCode: '',
  // Customer-visible review display: opt-in, default OFF.
  showReviewsOnProductPage: false,
  autoPublishReviews: false,
  seo: {
    metaTitle: '',
    metaDescription: '',
  },
};

// ── Component ──

export default function CommerceSettings() {
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();

  const [settings, setSettings] = useState<StoreSettings>(defaultSettings);

  const { data, isPending, isFetching } = useQuery({
    queryKey: qk.commerceSettings(projectId),
    queryFn: async () => {
      const response = await api.get(`/projects/${projectId}/ecommerce/settings`);
      return response.data?.data as StoreSettings;
    },
    enabled: Boolean(projectId),
  });

  useEffect(() => {
    if (data) {
      setSettings({ ...defaultSettings, ...data });
    }
  }, [data]);

  // Set when the server rejects a save because the settings changed under us
  // (409 VERSION_CONFLICT). Holds the server's current version so we can tell
  // the admin and offer a deliberate overwrite vs. discard-and-reload.
  const [conflict, setConflict] = useState<{ serverVersion: number } | null>(null);

  const saveMutation = useMutation({
    mutationFn: async (opts: { force?: boolean } = {}) => {
      // Optimistic concurrency: declare the version we loaded so the server
      // rejects (409) if someone else saved in the meantime. `force` — chosen
      // by the admin after acknowledging a conflict — omits the guard to
      // deliberately overwrite the newer server state.
      const payload = {
        ...settings,
        ...(opts.force ? {} : { expectedVersion: settings.version ?? 0 }),
      };
      await api.put(`/projects/${projectId}/ecommerce/settings`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.commerceSettings(projectId) });
      setConflict(null);
      toast.success('Store settings saved');
    },
    onError: (err) => {
      if (isVersionConflict(err)) {
        // Don't invalidate here — that would hydrate the form from the server
        // and silently discard the admin's in-progress edits. Surface a banner
        // and let them choose: overwrite, or discard-and-reload.
        setConflict({ serverVersion: conflictCurrentVersion(err) ?? settings.version ?? 0 });
        return;
      }
      toast.error(apiErrorMessage(err, 'Failed to save store settings'));
    },
  });

  // "Use the standard policy" — copies the Tyashin standard Return,
  // Replacement & Refund document + structured facts into THIS project.
  const applyPolicyMutation = useMutation({
    mutationFn: async (overwriteDoc: boolean) => {
      const res = await api.post(`/projects/${projectId}/ecommerce/policies/apply-standard`, {
        overwriteDoc,
      });
      return res.data as {
        docApplied: boolean;
        docSkippedExisting: boolean;
        factsApplied: boolean;
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: qk.commerceSettings(projectId) });
      toast.success(
        data.docSkippedExisting
          ? 'Standard policy facts applied — your existing policy document was kept.'
          : 'Standard policy applied: document + structured facts copied to this store.'
      );
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Failed to apply the standard policy')),
  });
  const applyingPolicy = applyPolicyMutation.isPending;
  const applyStandardPolicy = (overwriteDoc: boolean) => {
    if (
      overwriteDoc &&
      !window.confirm(
        'Replace your current return-policy document with a fresh copy of the Tyashin standard text? Your existing document will be overwritten.'
      )
    ) {
      return;
    }
    applyPolicyMutation.mutate(overwriteDoc);
  };

  const handleSave = () => {
    saveMutation.mutate({});
  };

  // ── Field updaters ──

  const set = <K extends keyof StoreSettings>(key: K, value: StoreSettings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const setSeo = (field: keyof SEO, value: string) => {
    setSettings((prev) => ({
      ...prev,
      seo: { ...prev.seo, [field]: value },
    }));
  };

  // ── Shipping Zones v2 ──

  // Rebase priorities to array index so priority === order in list.
  function rebasePriorities(zones: ShippingZone[]): ShippingZone[] {
    return zones.map((z, idx) => ({ ...z, priority: idx }));
  }

  const updateZoneAt = (idx: number, next: ShippingZone) => {
    setSettings((prev) => {
      const copy = prev.shippingZones.slice();
      copy[idx] = next;
      return { ...prev, shippingZones: rebasePriorities(copy) };
    });
  };

  const removeZoneAt = (idx: number) => {
    setSettings((prev) => {
      const copy = prev.shippingZones.slice();
      copy.splice(idx, 1);
      return { ...prev, shippingZones: rebasePriorities(copy) };
    });
  };

  const appendFromTemplate = (templateId: string) => {
    const tpl = ZONE_TEMPLATES.find((t) => t.id === templateId);
    if (!tpl) return;
    const newZones = tpl.build();
    setSettings((prev) => ({
      ...prev,
      shippingZones: rebasePriorities([...prev.shippingZones, ...newZones]),
    }));
    toast.success(`Added ${newZones.length} zone${newZones.length === 1 ? '' : 's'} from template`);
  };

  // ── Drag-to-reorder (native HTML5) ──
  // Keep drag state in a ref so React re-renders don't stomp on it mid-drag.
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const dragOverIdRef = useRef<string | null>(null);

  function reorderZones(fromId: string, toId: string) {
    if (fromId === toId) return;
    setSettings((prev) => {
      const zones = prev.shippingZones.slice();
      const fromIdx = zones.findIndex((z) => z.id === fromId);
      const toIdx = zones.findIndex((z) => z.id === toId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [moved] = zones.splice(fromIdx, 1);
      zones.splice(toIdx, 0, moved);
      return { ...prev, shippingZones: rebasePriorities(zones) };
    });
  }

  // ── Loading state ──
  // Only the FIRST load (nothing cached) shows a skeleton. Background refetches
  // keep the current form on screen and surface a quiet "Refreshing…" hint.

  if (isPending) {
    return (
      <div className="flex justify-center py-8">
        <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  // ── Render ──

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={`/projects/${projectId}`}>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Store Settings</h1>
            <p className="text-muted-foreground">Configure your e-commerce store preferences</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      {/* ── Concurrency conflict banner ──
          Shown when a save was rejected because the settings changed on the
          server while this admin was editing. We keep their unsaved edits and
          let them choose how to reconcile. */}
      {conflict && (
        <div className="flex flex-col gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <div className="text-sm">
              <p className="font-medium">
                These store settings changed elsewhere while you were editing.
              </p>
              <p className="text-amber-800">
                The server now has version {conflict.serverVersion} (you loaded{' '}
                {settings.version ?? 0}). Your edits are still here — overwrite to keep them, or
                discard to load the latest.
              </p>
            </div>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                // Discard local edits and hydrate from the server (the query's
                // useEffect re-runs on fresh data and resets `settings`).
                setConflict(null);
                queryClient.invalidateQueries({ queryKey: qk.commerceSettings(projectId) });
              }}
            >
              Discard &amp; load latest
            </Button>
            <Button
              size="sm"
              disabled={saveMutation.isPending}
              onClick={() => saveMutation.mutate({ force: true })}
            >
              Overwrite with my changes
            </Button>
          </div>
        </div>
      )}

      {/* Payment webhook health — only renders if something needs attention */}
      {projectId && <WebhookHealthBanner projectId={projectId} />}

      {/* Tabs */}
      <div className="relative">
        {isFetching && <RefreshingHint />}
        <Tabs defaultValue="general" className="space-y-4">
          <TabsList>
            <TabsTrigger value="general">
              <Store className="mr-2 h-4 w-4" />
              General
            </TabsTrigger>
            <TabsTrigger value="payment">
              <CreditCard className="mr-2 h-4 w-4" />
              Payment
            </TabsTrigger>
            <TabsTrigger value="shipping">
              <Truck className="mr-2 h-4 w-4" />
              Shipping
            </TabsTrigger>
            <TabsTrigger value="notifications">
              <Bell className="mr-2 h-4 w-4" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="developer">
              <Code className="mr-2 h-4 w-4" />
              Developer
            </TabsTrigger>
          </TabsList>

          {/* ─── General Tab ─── */}
          <TabsContent value="general" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Store Information</CardTitle>
                <CardDescription>Basic details about your store</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="storeName">Store Name</Label>
                    <Input
                      id="storeName"
                      value={settings.storeName}
                      onChange={(e) => set('storeName', e.target.value)}
                      placeholder="My Store"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="storeEmail">Store Email</Label>
                    <Input
                      id="storeEmail"
                      type="email"
                      value={settings.storeEmail}
                      onChange={(e) => set('storeEmail', e.target.value)}
                      placeholder="store@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="storePhone">Store Phone</Label>
                    <Input
                      id="storePhone"
                      value={settings.storePhone}
                      onChange={(e) => set('storePhone', e.target.value)}
                      placeholder="+1 555-0100"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Store Currency</Label>
                    <Select value={settings.currency} onValueChange={(v) => set('currency', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="INR">{'\u20b9'} INR — Indian Rupee</SelectItem>
                        <SelectItem value="USD">$ USD — US Dollar</SelectItem>
                        <SelectItem value="EUR">{'\u20ac'} EUR — Euro</SelectItem>
                        <SelectItem value="GBP">{'\u00a3'} GBP — British Pound</SelectItem>
                        <SelectItem value="CAD">C$ CAD — Canadian Dollar</SelectItem>
                        <SelectItem value="AUD">A$ AUD — Australian Dollar</SelectItem>
                        <SelectItem value="SGD">S$ SGD — Singapore Dollar</SelectItem>
                        <SelectItem value="AED">AED — UAE Dirham</SelectItem>
                        <SelectItem value="JPY">{'\u00a5'} JPY — Japanese Yen</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-muted-foreground text-xs">
                      This currency applies to all products, cart, and orders in your store.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Tax Configuration</CardTitle>
                <CardDescription>
                  Configure the tax name, rate, and display mode for your store. New products
                  inherit this rate by default.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="taxName">Tax Name</Label>
                    <Select
                      value={
                        COMMON_TAX_NAMES.some((t) => t.value === settings.taxName)
                          ? settings.taxName
                          : '_custom'
                      }
                      onValueChange={(val) => {
                        if (val === '_custom') {
                          // Keep current value but switch to custom mode
                          if (COMMON_TAX_NAMES.some((t) => t.value === settings.taxName)) {
                            set('taxName', '');
                          }
                        } else {
                          set('taxName', val);
                        }
                      }}
                    >
                      <SelectTrigger id="taxName">
                        <SelectValue placeholder="Select tax type" />
                      </SelectTrigger>
                      <SelectContent>
                        {COMMON_TAX_NAMES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.value} — {t.label}
                          </SelectItem>
                        ))}
                        <SelectItem value="_custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                    {!COMMON_TAX_NAMES.some((t) => t.value === settings.taxName) && (
                      <Input
                        value={settings.taxName}
                        onChange={(e) => set('taxName', e.target.value)}
                        placeholder="Enter custom tax name"
                        className="mt-2"
                      />
                    )}
                    <p className="text-muted-foreground text-xs">
                      Displayed on storefront, cart, and invoices.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="taxRate">Default Tax Rate (%)</Label>
                    <Input
                      id="taxRate"
                      type="number"
                      min={0}
                      max={100}
                      step={0.01}
                      value={settings.taxRate}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        set('taxRate', isNaN(val) ? 0 : Math.min(100, Math.max(0, val)));
                      }}
                      placeholder="0"
                    />
                    <p className="text-muted-foreground text-xs">
                      Products inherit this rate unless overridden.
                    </p>
                  </div>
                  <div className="flex items-center gap-3 pt-7">
                    <Switch
                      id="taxInclusive"
                      checked={settings.taxInclusive}
                      onCheckedChange={(checked) => set('taxInclusive', checked)}
                    />
                    <Label htmlFor="taxInclusive">Prices include tax</Label>
                  </div>
                </div>
                {settings.taxRate > 0 && (
                  <p className="text-muted-foreground bg-muted/50 rounded-lg p-3 text-sm">
                    {settings.taxInclusive
                      ? `Product prices include ${settings.taxName} at ${settings.taxRate}%. The ${settings.taxName} portion is shown as a breakup on the storefront.`
                      : `${settings.taxName} at ${settings.taxRate}% will be added on top of product prices at checkout.`}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  SEO
                </CardTitle>
                <CardDescription>Search engine optimization for your storefront</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="metaTitle">Meta Title</Label>
                  <Input
                    id="metaTitle"
                    value={settings.seo.metaTitle}
                    onChange={(e) => setSeo('metaTitle', e.target.value)}
                    placeholder="My Awesome Store"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="metaDescription">Meta Description</Label>
                  <Textarea
                    id="metaDescription"
                    value={settings.seo.metaDescription}
                    onChange={(e) => setSeo('metaDescription', e.target.value)}
                    placeholder="A brief description of your store for search engines..."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── Payment Tab ─── */}
          <TabsContent value="payment" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Payment Gateway</CardTitle>
                <CardDescription>
                  Choose and configure your primary payment processor
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="paymentGateway">Gateway</Label>
                  <Select
                    value={settings.paymentGateway}
                    onValueChange={(value: StoreSettings['paymentGateway']) =>
                      set('paymentGateway', value)
                    }
                  >
                    <SelectTrigger id="paymentGateway">
                      <SelectValue placeholder="Select gateway" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="stripe">Stripe</SelectItem>
                      <SelectItem value="razorpay">Razorpay</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {settings.paymentGateway !== 'none' && (
                  <>
                    <Separator />
                    {/* ── Live / Test mode toggle ── */}
                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <Label className="flex items-center gap-2">
                            <FlaskConical className="h-4 w-4" />
                            Payment Mode
                          </Label>
                          <p className="text-muted-foreground text-sm">
                            Switch between live transactions and sandbox test keys. Test mode uses
                            dummy cards — no real money moves. Refunds for existing orders always
                            use the mode they were paid in.
                          </p>
                        </div>
                        <div className="flex items-center gap-3 pt-1">
                          <span
                            className={
                              settings.paymentMode === 'live'
                                ? 'text-sm font-medium'
                                : 'text-muted-foreground text-sm'
                            }
                          >
                            Live
                          </span>
                          <Switch
                            checked={settings.paymentMode === 'test'}
                            onCheckedChange={(checked) =>
                              set('paymentMode', checked ? 'test' : 'live')
                            }
                          />
                          <span
                            className={
                              settings.paymentMode === 'test'
                                ? 'text-sm font-medium'
                                : 'text-muted-foreground text-sm'
                            }
                          >
                            Test
                          </span>
                        </div>
                      </div>

                      {settings.paymentMode === 'test' && (
                        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
                          <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
                          <div>
                            <strong className="block">Test mode is active.</strong>
                            <span>
                              New checkouts will use sandbox credentials. Show a visible banner on
                              your storefront to avoid confusing real shoppers. Switch back to{' '}
                              <em>Live</em> before launch.
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {settings.paymentGateway === 'stripe' && (
                  <>
                    <Separator />
                    {/* Live credentials */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold">Live credentials</h4>
                        {settings.paymentMode === 'live' && (
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-100">
                            Active
                          </span>
                        )}
                      </div>
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="stripePublishableKey">Publishable Key</Label>
                          <Input
                            id="stripePublishableKey"
                            type="password"
                            value={settings.stripePublishableKey}
                            onChange={(e) => set('stripePublishableKey', e.target.value)}
                            placeholder="pk_live_..."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="stripeSecretKey">Secret Key</Label>
                          <Input
                            id="stripeSecretKey"
                            type="password"
                            value={settings.stripeSecretKey}
                            onChange={(e) => set('stripeSecretKey', e.target.value)}
                            placeholder="sk_live_..."
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label htmlFor="stripeWebhookSecret">Webhook Secret</Label>
                          <Input
                            id="stripeWebhookSecret"
                            type="password"
                            value={settings.stripeWebhookSecret}
                            onChange={(e) => set('stripeWebhookSecret', e.target.value)}
                            placeholder="whsec_..."
                          />
                          <p className="text-muted-foreground text-xs">
                            Create a webhook in your Stripe dashboard pointing to{' '}
                            <code className="bg-muted rounded px-1 py-0.5 text-xs">
                              https://api.tyashin.com/api/v1/public/ecommerce/webhooks/stripe
                            </code>
                            , subscribe to{' '}
                            <code className="bg-muted rounded px-1 py-0.5 text-xs">
                              payment_intent.succeeded
                            </code>
                            ,{' '}
                            <code className="bg-muted rounded px-1 py-0.5 text-xs">
                              payment_intent.payment_failed
                            </code>
                            ,{' '}
                            <code className="bg-muted rounded px-1 py-0.5 text-xs">
                              charge.refunded
                            </code>
                            , and{' '}
                            <code className="bg-muted rounded px-1 py-0.5 text-xs">
                              charge.dispute.created
                            </code>
                            , and paste the signing secret here. Without this, online payments will
                            stay in <em>pending</em> after checkout.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />
                    {/* Test credentials */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="flex items-center gap-2 text-sm font-semibold">
                          <FlaskConical className="h-4 w-4" />
                          Test / sandbox credentials
                        </h4>
                        {settings.paymentMode === 'test' && (
                          <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900 dark:text-amber-100">
                            Active
                          </span>
                        )}
                      </div>
                      <p className="text-muted-foreground text-xs">
                        Flip the Stripe dashboard into <em>Test mode</em> (top-right toggle) and
                        copy the <code className="bg-muted rounded px-1 py-0.5">pk_test_…</code> /
                        <code className="bg-muted rounded px-1 py-0.5"> sk_test_…</code> keys. Test
                        webhooks can target the same URL — Stripe signs them with a separate secret.
                      </p>
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="stripeTestPublishableKey">Test Publishable Key</Label>
                          <Input
                            id="stripeTestPublishableKey"
                            type="password"
                            value={settings.stripeTestPublishableKey}
                            onChange={(e) => set('stripeTestPublishableKey', e.target.value)}
                            placeholder="pk_test_..."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="stripeTestSecretKey">Test Secret Key</Label>
                          <Input
                            id="stripeTestSecretKey"
                            type="password"
                            value={settings.stripeTestSecretKey}
                            onChange={(e) => set('stripeTestSecretKey', e.target.value)}
                            placeholder="sk_test_..."
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label htmlFor="stripeTestWebhookSecret">Test Webhook Secret</Label>
                          <Input
                            id="stripeTestWebhookSecret"
                            type="password"
                            value={settings.stripeTestWebhookSecret}
                            onChange={(e) => set('stripeTestWebhookSecret', e.target.value)}
                            placeholder="whsec_..."
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {settings.paymentGateway === 'razorpay' && (
                  <>
                    <Separator />
                    {/* Live credentials */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold">Live credentials</h4>
                        {settings.paymentMode === 'live' && (
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-100">
                            Active
                          </span>
                        )}
                      </div>
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="razorpayKeyId">Key ID</Label>
                          <Input
                            id="razorpayKeyId"
                            type="password"
                            value={settings.razorpayKeyId}
                            onChange={(e) => set('razorpayKeyId', e.target.value)}
                            placeholder="rzp_live_..."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="razorpayKeySecret">Key Secret</Label>
                          <Input
                            id="razorpayKeySecret"
                            type="password"
                            value={settings.razorpayKeySecret}
                            onChange={(e) => set('razorpayKeySecret', e.target.value)}
                            placeholder="Secret key"
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label htmlFor="razorpayWebhookSecret">Webhook Secret</Label>
                          <Input
                            id="razorpayWebhookSecret"
                            type="password"
                            value={settings.razorpayWebhookSecret}
                            onChange={(e) => set('razorpayWebhookSecret', e.target.value)}
                            placeholder="Webhook signing secret"
                          />
                          <p className="text-muted-foreground text-xs">
                            Create a webhook in your Razorpay dashboard pointing to{' '}
                            <code className="bg-muted rounded px-1 py-0.5 text-xs">
                              https://api.tyashin.com/api/v1/public/ecommerce/webhooks/razorpay
                            </code>
                            , subscribe to the{' '}
                            <code className="bg-muted rounded px-1 py-0.5 text-xs">
                              payment.captured
                            </code>{' '}
                            event, and paste the signing secret here. Without this, online payments
                            will stay in <em>pending</em> after checkout.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />
                    {/* Test credentials */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="flex items-center gap-2 text-sm font-semibold">
                          <FlaskConical className="h-4 w-4" />
                          Test / sandbox credentials
                        </h4>
                        {settings.paymentMode === 'test' && (
                          <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900 dark:text-amber-100">
                            Active
                          </span>
                        )}
                      </div>
                      <p className="text-muted-foreground text-xs">
                        Switch to the <em>Test Mode</em> tab in Razorpay Dashboard → Settings → API
                        Keys and generate a test key pair (
                        <code className="bg-muted rounded px-1 py-0.5">rzp_test_…</code>). Configure
                        a test webhook with its own signing secret — Razorpay keeps live and test
                        webhooks entirely separate.
                      </p>
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="razorpayTestKeyId">Test Key ID</Label>
                          <Input
                            id="razorpayTestKeyId"
                            type="password"
                            value={settings.razorpayTestKeyId}
                            onChange={(e) => set('razorpayTestKeyId', e.target.value)}
                            placeholder="rzp_test_..."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="razorpayTestKeySecret">Test Key Secret</Label>
                          <Input
                            id="razorpayTestKeySecret"
                            type="password"
                            value={settings.razorpayTestKeySecret}
                            onChange={(e) => set('razorpayTestKeySecret', e.target.value)}
                            placeholder="Test secret key"
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label htmlFor="razorpayTestWebhookSecret">Test Webhook Secret</Label>
                          <Input
                            id="razorpayTestWebhookSecret"
                            type="password"
                            value={settings.razorpayTestWebhookSecret}
                            onChange={(e) => set('razorpayTestWebhookSecret', e.target.value)}
                            placeholder="Test webhook signing secret"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Additional Payment Methods</CardTitle>
                <CardDescription>Enable alternative ways for customers to pay</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Cash on Delivery</Label>
                    <p className="text-muted-foreground text-sm">
                      Allow customers to pay when the order is delivered
                    </p>
                  </div>
                  <Switch
                    checked={settings.enableCod}
                    onCheckedChange={(checked) => set('enableCod', checked)}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div>
                    <Label>WhatsApp Checkout</Label>
                    <p className="text-muted-foreground text-sm">
                      Let customers complete orders via WhatsApp
                    </p>
                  </div>
                  <Switch
                    checked={settings.enableWhatsapp}
                    onCheckedChange={(checked) => set('enableWhatsapp', checked)}
                  />
                </div>

                {settings.enableWhatsapp && (
                  <div className="space-y-2 pl-1">
                    <Label htmlFor="whatsappNumber">WhatsApp Number</Label>
                    <Input
                      id="whatsappNumber"
                      value={settings.whatsappNumber}
                      onChange={(e) => set('whatsappNumber', e.target.value)}
                      placeholder="+91 98765 43210"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── Shipping Tab ─── */}
          <TabsContent value="shipping" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Checkout Options</CardTitle>
                <CardDescription>Control what information is required at checkout</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Require Shipping Address</Label>
                    <p className="text-muted-foreground text-sm">
                      Customers must provide a shipping address
                    </p>
                  </div>
                  <Switch
                    checked={settings.requireShippingAddress}
                    onCheckedChange={(checked) => set('requireShippingAddress', checked)}
                  />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Require Phone Number</Label>
                    <p className="text-muted-foreground text-sm">
                      Customers must provide a phone number
                    </p>
                  </div>
                  <Switch
                    checked={settings.requirePhone}
                    onCheckedChange={(checked) => set('requirePhone', checked)}
                  />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Enable Guest Checkout</Label>
                    <p className="text-muted-foreground text-sm">
                      Allow customers to check out without creating an account
                    </p>
                  </div>
                  <Switch
                    checked={settings.enableGuestCheckout}
                    onCheckedChange={(checked) => set('enableGuestCheckout', checked)}
                  />
                </div>
              </CardContent>
            </Card>

            <ShippingCountriesCard
              selected={settings.shippingCountries ?? []}
              onChange={(countries) => set('shippingCountries', countries)}
            />

            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <CardTitle>Shipping Zones</CardTitle>
                    <CardDescription>
                      Zones are checked top-to-bottom. The first match sets the rate. Drag to
                      reorder. When no zone matches a customer's address, checkout is blocked (we
                      never silently ship free).
                    </CardDescription>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Plus className="mr-2 h-4 w-4" />
                        Add zones
                        <ChevronDown className="ml-1 h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[320px]">
                      <DropdownMenuLabel>Start from a template</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {ZONE_TEMPLATES.map((t) => (
                        <DropdownMenuItem
                          key={t.id}
                          onSelect={() => appendFromTemplate(t.id)}
                          className="flex-col items-start gap-0.5"
                        >
                          <span className="font-medium">{t.label}</span>
                          <span className="text-muted-foreground text-xs">{t.description}</span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {settings.shippingZones.length === 0 && (
                  <div className="rounded-lg border border-dashed py-8 text-center">
                    <p className="text-muted-foreground text-sm">
                      No shipping zones configured yet.
                    </p>
                    <p className="text-muted-foreground mt-1 text-xs">
                      Use <strong>Add zones → India / US / Canada / EU</strong> to get started with
                      a sensible preset, or start from a blank zone.
                    </p>
                  </div>
                )}

                {settings.shippingZones.map((zone, idx) => (
                  <ShippingZoneEditor
                    key={zone.id}
                    zone={zone}
                    positionLabel={`${idx + 1}`}
                    isDragging={draggingId === zone.id}
                    onDragStart={() => setDraggingId(zone.id)}
                    onDragEnter={() => {
                      dragOverIdRef.current = zone.id;
                    }}
                    onDragEnd={() => {
                      const over = dragOverIdRef.current;
                      if (draggingId && over && draggingId !== over) {
                        reorderZones(draggingId, over);
                      }
                      setDraggingId(null);
                      dragOverIdRef.current = null;
                    }}
                    onChange={(next) => updateZoneAt(idx, next)}
                    onDelete={() => removeZoneAt(idx)}
                  />
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── Notifications Tab ─── */}
          <TabsContent value="notifications" className="space-y-4">
            {/* Customer-facing emails */}
            <Card>
              <CardHeader>
                <CardTitle>Customer emails</CardTitle>
                <CardDescription>
                  Automated emails sent to customers as their order progresses. Each email counts
                  toward your monthly email usage.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Order confirmation</Label>
                    <p className="text-muted-foreground text-sm">
                      Sent when an order is placed (COD) or when payment is confirmed
                    </p>
                  </div>
                  <Switch
                    checked={settings.orderConfirmationEmail}
                    onCheckedChange={(checked) => set('orderConfirmationEmail', checked)}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Order shipped</Label>
                    <p className="text-muted-foreground text-sm">
                      Sent when you mark the order as shipped — includes tracking info when
                      available
                    </p>
                  </div>
                  <Switch
                    checked={settings.orderShippedEmail}
                    onCheckedChange={(checked) => set('orderShippedEmail', checked)}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Order delivered</Label>
                    <p className="text-muted-foreground text-sm">
                      Sent when the order is marked delivered — a nice touch that builds repeat
                      buyers
                    </p>
                  </div>
                  <Switch
                    checked={settings.orderDeliveredEmail}
                    onCheckedChange={(checked) => set('orderDeliveredEmail', checked)}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Order cancelled / refunded</Label>
                    <p className="text-muted-foreground text-sm">
                      Sent if you cancel or refund an order, with the reason you provide
                    </p>
                  </div>
                  <Switch
                    checked={settings.orderCancelledEmail}
                    onCheckedChange={(checked) => set('orderCancelledEmail', checked)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Marketing emails — opt-in */}
            <Card>
              <CardHeader>
                <CardTitle>Marketing emails (opt-in)</CardTitle>
                <CardDescription>
                  Automated marketing nudges to your customers. These are{' '}
                  <strong>off by default</strong>. Turn on only what you're comfortable with — each
                  toggle below explains exactly what the customer will receive, when, and how they
                  can opt out.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <Label>Abandoned cart recovery</Label>
                    <p className="text-muted-foreground text-sm">
                      If a shopper adds items to cart and gives us their email but doesn't check
                      out, we'll email them <strong>once</strong>, between 24 and 168 hours after
                      capture, with a link to resume checkout. The email includes an unsubscribe
                      link that silences future recovery emails for that cart.
                    </p>
                    <p className="text-muted-foreground text-xs">
                      Counts toward your monthly email usage. One email per abandoned cart — never
                      repeated.
                    </p>
                  </div>
                  <Switch
                    checked={settings.cartRecoveryEmail}
                    onCheckedChange={(checked) => set('cartRecoveryEmail', checked)}
                  />
                </div>

                <Separator />

                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-1">
                    <Label>Post-delivery review ask</Label>
                    <p className="text-muted-foreground text-sm">
                      After you mark an order as <em>delivered</em>, we'll email the customer{' '}
                      <strong>once</strong>, after the delay below, asking them to leave a review.
                      Links to your public review form at <code>/reviews/&lt;orderNumber&gt;</code>.
                      Reviews are still moderated by you before they appear.
                    </p>
                    <p className="text-muted-foreground text-xs">
                      Counts toward your monthly email usage. One email per order — never repeated.
                      Orders delivered more than 30 days ago are never asked.
                    </p>
                    {settings.reviewAskEmail && (
                      <div className="flex flex-wrap items-center gap-2 pt-2">
                        <Label htmlFor="review-delay" className="text-xs">
                          Send
                        </Label>
                        <Input
                          id="review-delay"
                          type="number"
                          min={1}
                          max={30}
                          className="h-8 w-20"
                          value={settings.reviewAskDelayDays}
                          onChange={(e) =>
                            set(
                              'reviewAskDelayDays',
                              Math.max(1, Math.min(30, parseInt(e.target.value, 10) || 5))
                            )
                          }
                        />
                        <span className="text-muted-foreground text-xs">
                          day(s) after the order is
                        </span>
                        <select
                          value={settings.reviewAskTrigger}
                          onChange={(e) =>
                            set(
                              'reviewAskTrigger',
                              e.target.value as StoreSettings['reviewAskTrigger']
                            )
                          }
                          className="border-border bg-background h-8 rounded-md border px-2 text-xs"
                        >
                          <option value="paid">paid</option>
                          <option value="shipped">shipped</option>
                          <option value="delivered">delivered</option>
                        </select>
                        <p className="text-muted-foreground w-full text-xs">
                          Pick <em>paid</em> or <em>shipped</em> if you don&apos;t reliably mark
                          orders as delivered — otherwise the ask never goes out.
                        </p>
                      </div>
                    )}
                  </div>
                  <Switch
                    checked={settings.reviewAskEmail}
                    onCheckedChange={(checked) => set('reviewAskEmail', checked)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Product reviews — storefront display + moderation */}
            <Card>
              <CardHeader>
                <CardTitle>Product reviews</CardTitle>
                <CardDescription>
                  What shoppers see on your product pages. Moderate individual reviews from the
                  Reviews page.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-1">
                    <Label>Show reviews on product pages</Label>
                    <p className="text-muted-foreground text-sm">
                      Shoppers see the star rating and published reviews on each product page, and
                      Google becomes eligible to show{' '}
                      <strong>star ratings in search results</strong> for products that have
                      reviews. When off, reviews stay collected but hidden.
                    </p>
                  </div>
                  <Switch
                    checked={settings.showReviewsOnProductPage}
                    onCheckedChange={(checked) => set('showReviewsOnProductPage', checked)}
                  />
                </div>

                <Separator />

                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-1">
                    <Label>Auto-publish new reviews</Label>
                    <p className="text-muted-foreground text-sm">
                      Verified-buyer reviews go live immediately without waiting for your approval.
                      When off (recommended), new reviews sit in the moderation queue until you
                      publish them.
                    </p>
                  </div>
                  <Switch
                    checked={settings.autoPublishReviews}
                    onCheckedChange={(checked) => set('autoPublishReviews', checked)}
                  />
                </div>

                <Separator />

                <div className="space-y-1">
                  <Label htmlFor="review-reward-code">Reward reviewers with a coupon</Label>
                  <p className="text-muted-foreground text-sm">
                    When a review is <em>published</em>, the reviewer gets a thank-you email with
                    this coupon code — once per review, from any channel (email link, product page,
                    review form). Must match one of your existing active coupon codes. Leave empty
                    for no reward.
                  </p>
                  <Input
                    id="review-reward-code"
                    placeholder="e.g. THANKYOU10"
                    className="mt-2 max-w-xs uppercase"
                    value={settings.reviewRewardCouponCode}
                    onChange={(e) => set('reviewRewardCouponCode', e.target.value.toUpperCase())}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Returns, replacements & refunds */}
            <Card>
              <CardHeader>
                <CardTitle>Returns, replacements &amp; refunds</CardTitle>
                <CardDescription>
                  Two parts: the <strong>policy document</strong> shoppers read (your{' '}
                  <code>/return-policy</code> page, editable under Legal Pages) and the{' '}
                  <strong>structured facts</strong> below, which power the policy summary on product
                  pages and Google merchant-listing data. Applying the Tyashin standard copies both
                  into <em>your</em> account — you can edit or replace them any time.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-cream/50 border-border rounded-md border p-4">
                  <p className="text-sm font-medium">
                    Tyashin standard policy: <em>all sales final</em> — no returns or exchanges,
                    with free replacement (or refund) for damaged, defective, or wrong items
                    reported within 7 days of delivery. Lawful under the Consumer Protection Act,
                    2019, and written to protect your store.
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={applyingPolicy}
                      onClick={() => applyStandardPolicy(false)}
                    >
                      {applyingPolicy ? 'Applying…' : 'Use the standard policy'}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={applyingPolicy}
                      onClick={() => applyStandardPolicy(true)}
                    >
                      Re-apply &amp; replace my document
                    </Button>
                  </div>
                  <p className="text-muted-foreground mt-2 text-xs">
                    &ldquo;Use the standard policy&rdquo; keeps a document you already wrote and
                    only fills what&apos;s missing; the second button overwrites the document with a
                    fresh copy of the standard text.
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Label className="text-sm">Returns are</Label>
                  <select
                    value={settings.returnPolicy?.category ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) set('returnPolicy', undefined);
                      else if (v === 'not-permitted') {
                        set('returnPolicy', {
                          ...settings.returnPolicy,
                          category: 'not-permitted',
                          defectiveItemWindowDays:
                            settings.returnPolicy?.defectiveItemWindowDays ?? 7,
                        });
                      } else {
                        set('returnPolicy', {
                          category: 'finite',
                          merchantReturnDays: settings.returnPolicy?.merchantReturnDays ?? 7,
                          returnFees: settings.returnPolicy?.returnFees ?? 'customer-paid',
                          defectiveItemWindowDays:
                            settings.returnPolicy?.defectiveItemWindowDays ?? 7,
                        });
                      }
                    }}
                    className="border-border bg-background h-9 rounded-md border px-2 text-sm"
                  >
                    <option value="">not configured</option>
                    <option value="not-permitted">not accepted (all sales final)</option>
                    <option value="finite">accepted within a window</option>
                  </select>
                  {settings.returnPolicy?.category === 'finite' && (
                    <>
                      <Input
                        type="number"
                        min={1}
                        max={365}
                        className="h-9 w-20"
                        value={settings.returnPolicy.merchantReturnDays ?? 7}
                        onChange={(e) =>
                          set('returnPolicy', {
                            ...settings.returnPolicy!,
                            merchantReturnDays: Math.max(
                              1,
                              Math.min(365, parseInt(e.target.value, 10) || 7)
                            ),
                          })
                        }
                      />
                      <span className="text-muted-foreground text-sm">days,</span>
                      <select
                        value={settings.returnPolicy.returnFees ?? 'customer-paid'}
                        onChange={(e) =>
                          set('returnPolicy', {
                            ...settings.returnPolicy!,
                            returnFees: e.target.value as 'free' | 'customer-paid',
                          })
                        }
                        className="border-border bg-background h-9 rounded-md border px-2 text-sm"
                      >
                        <option value="customer-paid">customer pays return shipping</option>
                        <option value="free">free return shipping</option>
                      </select>
                    </>
                  )}
                </div>
                {settings.returnPolicy && (
                  <p className="text-muted-foreground text-xs">
                    Damaged/defective/wrong items: replacement or refund when reported within{' '}
                    {settings.returnPolicy.defectiveItemWindowDays ?? 7} days of delivery (set by
                    the policy document). These facts must match what your policy document says —
                    Google reads both.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Merchant-facing emails */}
            <Card>
              <CardHeader>
                <CardTitle>Merchant alerts</CardTitle>
                <CardDescription>
                  Emails sent to your store email (
                  {settings.storeEmail || 'set your store email above'})
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>New order alert</Label>
                    <p className="text-muted-foreground text-sm">
                      Notify you each time a new order is placed
                    </p>
                  </div>
                  <Switch
                    checked={settings.newOrderAlertEmail}
                    onCheckedChange={(checked) => set('newOrderAlertEmail', checked)}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Low stock alert</Label>
                    <p className="text-muted-foreground text-sm">
                      Notify you when product stock falls below the threshold
                    </p>
                  </div>
                  <Switch
                    checked={settings.lowStockAlertEmail}
                    onCheckedChange={(checked) => set('lowStockAlertEmail', checked)}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── Developer Tab ─── */}
          <TabsContent value="developer" className="space-y-4">
            <StorefrontSpecCard projectId={projectId!} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ── Storefront Integration Spec Card ──

function StorefrontSpecCard({ projectId }: { projectId: string }) {
  const [copied, setCopied] = useState(false);

  const { data: specData, isLoading: specLoading } = useQuery({
    queryKey: STOREFRONT_SPEC_KEY,
    queryFn: async () => {
      const response = await api.get('/generator/storefront-spec');
      return response.data?.data as {
        version: string;
        spec: string;
        usage: string;
        placeholders: Record<string, string>;
      };
    },
  });

  const { data: projectData } = useQuery({
    queryKey: qk.project(projectId),
    queryFn: async () => {
      const response = await api.get(`/projects/${projectId}`);
      return response.data?.data as { apiKey?: string; slug?: string };
    },
    enabled: Boolean(projectId),
  });

  const handleCopy = async () => {
    if (!specData?.spec) return;
    try {
      await navigator.clipboard.writeText(specData.spec);
      setCopied(true);
      toast.success('Spec copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy — try selecting the text manually');
    }
  };

  return (
    <>
      {/* Instructions Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="h-5 w-5" />
            Storefront Integration Spec
          </CardTitle>
          <CardDescription>
            Use this spec to build a Tyashin-compatible storefront with any AI code generator or
            manually.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted/50 space-y-3 rounded-lg border p-4 text-sm">
            <p className="font-medium">How to use this spec:</p>
            <ol className="text-muted-foreground list-inside list-decimal space-y-2">
              <li>Copy the integration spec below</li>
              <li>
                Paste it into any AI code generator (ChatGPT, Claude, Lovable, v0, Bolt, Cursor,
                etc.)
              </li>
              <li>Add your design requirements alongside the spec</li>
              <li>Replace the placeholder values with your project details (see below)</li>
              <li>The generated storefront will be fully compatible with Tyashin&apos;s backend</li>
            </ol>
          </div>

          {/* Placeholder values */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Your project values:</p>
            <div className="grid gap-2 md:grid-cols-2">
              <div className="rounded border p-3">
                <p className="text-muted-foreground text-xs font-medium">STOREFRONT_API_URL</p>
                <code className="break-all text-xs">
                  {`${window.location.origin}/api/v1/public/ecommerce`}
                </code>
              </div>
              <div className="rounded border p-3">
                <p className="text-muted-foreground text-xs font-medium">API_KEY</p>
                <code className="break-all text-xs">
                  {projectData?.apiKey || 'Go to Settings → API Keys'}
                </code>
              </div>
            </div>
          </div>

          {specData && (
            <p className="text-muted-foreground text-xs">Spec version: {specData.version}</p>
          )}
        </CardContent>
      </Card>

      {/* Spec Content Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Integration Spec</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopy}
              disabled={specLoading || !specData}
            >
              {copied ? (
                <>
                  <Check className="mr-2 h-4 w-4 text-green-600" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="mr-2 h-4 w-4" />
                  Copy Spec
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {specLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
            </div>
          ) : specData?.spec ? (
            <pre className="bg-muted/50 max-h-[500px] overflow-auto whitespace-pre-wrap rounded-lg border p-4 font-mono text-xs">
              {specData.spec}
            </pre>
          ) : (
            <p className="text-muted-foreground py-4 text-center text-sm">
              Failed to load integration spec. Please try again.
            </p>
          )}
        </CardContent>
      </Card>
    </>
  );
}
